import { type FC } from "react";

import { EmptySlots } from "~/components/inspector/expandable/EmptySlots";

import { type CollapsedValueRow } from "../traverseValue/ValueInspectorRowInfo";
import { type ValuePath } from "../traverseValue/ValuePath";
import { CollapsedValueName } from "./CollapsedValueName";
import { NonExpandableValue } from "./NonExpandableValue";

/**
 * Renders the fields of an collapsed object. For example, a simple field like `a` is rendered
 * outright, while "expandable" fields like `c` and `d` are summarized:
 *
 *     > myCell = { a: "b", c: Object, d: Array(2) }
 *
 */
export const CollapsedShallowRowValue: FC<{
	row: CollapsedValueRow;
}> = ({ row }) => {
	const values = row.fields.map((field, i) => {
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		const lastComponent = field.path[field.path.length - 1]!;

		let prelude = <></>;
		switch (lastComponent.type) {
			case "Cell":
				prelude = (
					<>
						<CollapsedValueName component={lastComponent} />
						&nbsp;=&nbsp;
					</>
				);
				break;
			case "ArrayElement":
			case "SetField":
				break;
			case "ObjectField":
				prelude = (
					<>
						<CollapsedValueName component={lastComponent} />
						:&nbsp;
					</>
				);
				break;
			case "MapField":
				prelude = (
					<>
						<CollapsedValueName component={lastComponent} />
						&nbsp;{"=>"}&nbsp;
					</>
				);
				break;
		}

		let el: JSX.Element;
		switch (field.type) {
			case "NonExpandableValueShallow":
				el = (
					<>
						{prelude}
						<NonExpandableValue typeInfo={field.valueTypeInfo} />
					</>
				);
				break;
			case "EmptyArraySlotValueShallow":
				el = (
					<>
						{prelude}
						<EmptySlots n={field.emptySlots} />
					</>
				);
				break;
			case "ExpandableValueShallow":
				el = (
					<>
						{prelude}
						{constructorName(lastComponent)}
					</>
				);
				break;
		}

		const spacer = i === row.fields.length - 1 ? "" : ", ";
		return (
			<span key={i}>
				{el}
				{spacer}
			</span>
		);
	});
	return <>{values}</>;
};

const constructorName = (value: ValuePath[number]) => {
	switch (value.typeInfo.typeTag) {
		case "Arrayish":
			return `Array(${(value.value as Array<never>).length || 0})`;
		case "Map":
			return `Map(${(value.value as Map<never, never>).size || 0})`;
		case "Set":
			return `Set(${(value.value as Set<never>).size || 0})`;
		default:
			return "Object";
	}
};
